/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useIntl } from 'gatsby-plugin-react-intl';

import Layout from '../../Components/Core/Layout';
import SEO from '../../Components/Core/Seo';
import BackToTop from '../../Components/Core/BackToTop';
import PageBuilder from '../../Components/Core/PageBuilder';
import BreadCrumbs from '../../Components/Custom/BreadCrumbs';
import StaticSplitWidthHeroBanner from '../../Components/Custom/Banners/StaticSplitWidth';
import {
  SpecialOffersBanner,
  DigitalBankBanner,
} from '../../Components/Custom/AppBanner';
import { InterestRates } from '../../Components/Custom/TitleImageWithIconList';
import {
  Spotlight,
  FeatureCarousel,
  AccordionSection,
  OverflowCarousel,
  SlideCarousel,
} from '../../Components/Custom/Sections';
import { PointsTileCards } from '../../Components/Custom/Card-Sections/PointsTiles';
import ImageBanner from '../../Components/Custom/Banners/ImageBanner';
import OptimumStoresBanner from '../../Components/Custom/Optimum/OptimumStoresBanner';
import RatingBanner from '../../Components/Custom/Banners/RatingBanner';
import SimpleImageBanner from '../../Components/Custom/Banners/SimpleImageBanner';
import StatementSection from '../../Components/Custom/StatementSection';
import { ProductCards } from '../../Components/Custom/Card-Sections';
import LegalDetails from '../../Components/Custom/LegalDetails';
import CarouselGallery from '../../Components/Custom/Gallery/Carousel';
import OptimumProductCards from '../../Components/Custom/Optimum/OptimumProductCards';
import DigitalProductBanner from '../../Components/Custom/DigitalProduct/DigitalProductBanner';
import { containerSection } from '../../utils/variation';

const pageTemplate = {
  'pcma-hero': StaticSplitWidthHeroBanner,
  'static-split-width-content': StaticSplitWidthHeroBanner,
  'special-offers': SpecialOffersBanner,
  'interest-rates': InterestRates,
  'spotlight-section': Spotlight,
  'points-feature-section': PointsTileCards,
  'feature-carousel': FeatureCarousel,
  'image-banner': ImageBanner,
  'pc-optimum-store-banner': OptimumStoresBanner,
  'rating-banner': RatingBanner,
  'simple-image-banner': SimpleImageBanner,
  'accordion-section': AccordionSection,
  'statement-section': StatementSection,
  'product-block': ProductCards,
  'legal-details': LegalDetails,
  'overflow-carousel': OverflowCarousel,
  'gallery-carousel': CarouselGallery,
  'digital-bank-banner': DigitalBankBanner,
  'optimum-product-block': OptimumProductCards,
  'slide-carousel': SlideCarousel,
  'digital-product-banner': DigitalProductBanner,
  ...containerSection,
};

const CreditCardPageTemplate = ({ location, data }) => {
  const {
    contentfulCreditCardPageLayout: {
      languageToggle,
      promoCode,
      customHeader,
      sections,
      customFooter,
      pageSlug,
      internal: { type },
    },
  } = data;

  const intl = useIntl();

  return (
    <Layout
      location={location}
      header={customHeader}
      footer={customFooter}
      languageToggle={languageToggle}
      promoCode={promoCode}
      contentType={type}
    >
      <BreadCrumbs
        className="end-chevron credit-card"
        crumbs={[
          {
            label: `${intl.formatMessage({ id: 'creditCard' })}`,
            slug: '/credit-cards',
            active: !pageSlug.includes('index'),
          },
        ]}
      />
      <PageBuilder
        sections={sections}
        template={pageTemplate}
        pageSlug={pageSlug}
      />
      <BackToTop />
    </Layout>
  );
};

CreditCardPageTemplate.propTypes = {
  data: PropTypes.shape().isRequired,
  location: PropTypes.shape().isRequired,
};

export default CreditCardPageTemplate;

export const query = graphql`
  query CreditCardBySlug($locale: String = "en-CA", $contentful_id: String!) {
    contentfulCreditCardPageLayout(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $locale }
    ) {
      pageSlug
      pageTitle
      metaDescription
      languageToggle
      promoCode
      displayInSiteMap
      internal {
        type
      }
      ...global_credit_footer
      ...global_credit_header
      sections {
        ...hero_banner
        ...statement_section
        ...titleOrImageWithIconLists
        ...ticker_section
        ...product_chart
        ...calculator
        ...legal_details
        ...section_container
        ...simple_image_banner
        ...pro_tip
        ...app_banner
        ...app_banner_stepper
        ...gallery
        ...hero_banner
        ...paragraph
        ...product_chart
        ...table
        ...video_item
        ...optimum_stores_banner
      }
      schema {
        _context
        _type
        itemListElement {
          _type
          position
          name
          item
        }
        mainEntity {
          _type
          name
          acceptedAnswer {
            _type
            text
          }
        }
      }
    }
  }
`;

export const Head = ({ location, data, pageContext }) => {
  // remove outer null objects inside schema
  let cleanedSchema;

  if (data.contentfulCreditCardPageLayout.schema) {
    cleanedSchema = Object.fromEntries(
      Object.entries(data.contentfulCreditCardPageLayout.schema).filter(
        // eslint-disable-next-line no-unused-vars
        ([_, v]) => v != null,
      ),
    );
  }
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `https://www.pcfinancial.ca/${pageContext.language}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Credit Cards',
        item: `https://www.pcfinancial.ca/${pageContext.language}/credit-cards/`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: data.contentfulCreditCardPageLayout.pageTitle,
        item: `https://www.pcfinancial.ca${location.pathname}`,
      },
    ],
  };
  return (
    <SEO
      data={data.contentfulCreditCardPageLayout}
      pageContext={pageContext}
      location={location}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      {cleanedSchema && (
        <script type="application/ld+json">
          {JSON.stringify(cleanedSchema)}
        </script>
      )}
    </SEO>
  );
};
